import React from 'react';
import { View, StyleSheet, Platform, TouchableOpacity, useWindowDimensions, Image, TextStyle, ViewStyle, ImageStyle } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { Typography } from '../ui';
import { fonts } from '../../styles';
import { useTheme } from '../../context/ThemeContext';
import { useI18n } from '../../i18n';
import { openApp } from './openApp';
import { landingLayout } from './constants';
import { fontWeights } from '../../styles/typographyTokens';

import { ACCENT_TEXT, accentTextOn, ON_ACCENT_TEXT } from '../../styles/accentText';
const heroImage = require('../../../assets/generated/agape_monk_misty_mountains_deck.jpg');

type HeroAction = {
  labelKey: string;
  path: string;
  icon: keyof typeof Ionicons.glyphMap;
  variant: 'primary' | 'secondary';
};

const ACTIONS: HeroAction[] = [
  { labelKey: 'startPractice', path: '/practices', icon: 'radio-outline', variant: 'primary' },
  { labelKey: 'viewCalendar', path: '/lives', icon: 'calendar-outline', variant: 'secondary' },
];

const FLOATING_ICONS: Array<{ icon: keyof typeof Ionicons.glyphMap; color: string; bg: string; style: ViewStyle }> = [
  { icon: 'radio-outline', color: '#E85D04', bg: '#FFF3EA', style: { top: 24, left: 26 } },
  { icon: 'people-outline', color: '#6B5CE7', bg: '#F0EDFF', style: { right: 4, top: 180 } },
  { icon: 'calendar-outline', color: '#2D8A4E', bg: '#EAF8EE', style: { left: 16, bottom: 54 } },
];

export const Hero: React.FC = () => {
  const { width } = useWindowDimensions();
  const { colors: themeColors, isDark } = useTheme();
  const { t } = useI18n('landing');
  const isMobile = width < 768;
  const isDesktop = width >= 1024;
  const imageSize = isMobile ? Math.min(width - 72, 320) : isDesktop ? 420 : 340;
  const secondarySurface = isDark ? themeColors.surfaceElevated : '#FFFFFF';
  const secondaryBorder = isDark ? themeColors.border : '#EFE7DE';
  const photoBorder = isDark ? '#1A1A1A' : '#FFFFFF';
  const mutedIconColor = isDark ? themeColors.textSecondary : '#9A6A42';

  return (
    <View style={[styles.hero, { backgroundColor: themeColors.pageBg, borderBottomColor: isDark ? themeColors.borderSubtle : '#F0EAE4' }, isMobile && styles.heroMobile]}>
      <View style={[styles.inner, isMobile && styles.innerMobile]}>
        <View style={[styles.copyCol, isMobile && styles.copyColMobile]}>
          <View style={styles.overlineRow}>
            <View style={[styles.overlineDot, { backgroundColor: themeColors.fire }]} />
            <Typography style={[styles.overline, { color: ACCENT_TEXT }]}>{t('landingV2.hero.overline')}</Typography>
          </View>

          <Typography style={[styles.title, { color: themeColors.textPrimary }, isMobile && styles.titleMobile]}>
            {t('landingV2.hero.titleLine1')}{'\n'}{t('landingV2.hero.titleLine2Prefix')}{' '}
            <Typography style={[styles.title, styles.titleAccent, { color: ACCENT_TEXT }, isMobile && styles.titleMobile]}>{t('landingV2.hero.titleAccent')}</Typography>
          </Typography>

          <Typography style={[styles.lead, { color: themeColors.textSecondary }, isMobile && styles.leadMobile]}>
            {t('landingV2.hero.description1')}
          </Typography>
          <Typography style={[styles.lead, { color: themeColors.textSecondary }, isMobile && styles.leadMobile]}>
            {t('landingV2.hero.description2')}
          </Typography>

          <View style={[styles.actions, isMobile && styles.actionsMobile]}>
            {ACTIONS.map((action) => {
              const isPrimary = action.variant === 'primary';
              return (
                <TouchableOpacity
                  key={action.path}
                  activeOpacity={0.82}
                  onPress={() => openApp(action.path)}
                  style={[
                    styles.actionBtn,
                    isPrimary
                      ? [styles.actionPrimary, { backgroundColor: themeColors.fire, borderColor: themeColors.fire }]
                      : [styles.actionSecondary, { backgroundColor: secondarySurface, borderColor: secondaryBorder }],
                    isMobile && styles.actionMobile
                  ]}
                >
                  <Ionicons name={action.icon} size={18} color={isPrimary ? ON_ACCENT_TEXT : mutedIconColor} />
                  <View style={styles.actionTextWrap}>
                    <Typography style={[styles.actionLabel, { color: themeColors.textPrimary }, isPrimary && styles.actionLabelPrimary]}>{t(`landingV2.hero.actions.${action.labelKey}`)}</Typography>
                  </View>
                  <Ionicons name="arrow-forward" size={16} color={isPrimary ? ON_ACCENT_TEXT : themeColors.textPrimary} />
                </TouchableOpacity>
              );
            })}
          </View>
        </View>

        <View style={[styles.visualCol, isMobile && styles.visualColMobile]}>
          <View style={[styles.orbit, { width: imageSize + 46, height: imageSize + 46, borderRadius: (imageSize + 46) / 2, borderColor: isDark ? 'rgba(255,112,32,0.22)' : '#F2DFCE' }]}>
            <View style={[styles.photoShell, { width: imageSize, height: imageSize, borderRadius: imageSize / 2, borderColor: photoBorder, backgroundColor: isDark ? themeColors.surface : '#F8F2EC' }]}>
              <Image source={heroImage} style={styles.heroImage as ImageStyle} resizeMode="cover" />
            </View>
            {FLOATING_ICONS.map((item) => (
              <View key={item.icon} style={[styles.floatIcon, { backgroundColor: isDark ? themeColors.surfaceElevated : item.bg, borderColor: photoBorder }, item.style]}>
                <Ionicons name={item.icon} size={26} color={item.color} />
              </View>
            ))}
          </View>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  hero: {
    paddingTop: Platform.OS === 'web' ? 138 : 112,
    paddingBottom: 38,
    borderBottomWidth: 1,
    borderBottomColor: '#F0EAE4',
  } as ViewStyle,
  heroMobile: {
    paddingTop: 104,
    paddingBottom: 34,
  } as ViewStyle,
  inner: {
    width: '100%',
    maxWidth: landingLayout.maxWidth,
    marginHorizontal: 'auto',
    paddingHorizontal: landingLayout.paddingH,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 56,
  } as ViewStyle,
  innerMobile: {
    flexDirection: 'column',
    paddingHorizontal: landingLayout.paddingH,
    gap: 38,
  } as ViewStyle,
  copyCol: {
    flex: 1,
    maxWidth: 560,
  } as ViewStyle,
  copyColMobile: {
    flex: 0,
    flexBasis: 'auto',
    flexShrink: 0,
    width: '100%',
    maxWidth: '100%',
  } as ViewStyle,
  overlineRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 20,
  } as ViewStyle,
  overlineDot: {
    width: 5,
    height: 5,
    borderRadius: 3,
    backgroundColor: '#E85D04',
  },
  overline: {
    color: accentTextOn('#E85D04'),
    fontFamily: fonts.display,
    fontSize: 12,
    fontWeight: fontWeights.bold,
    letterSpacing: 2.4,
    textTransform: 'uppercase',
  } as TextStyle,
  title: {
    color: '#1C1713',
    fontFamily: fonts.display,
    fontSize: Platform.OS === 'web' ? 48 : 38,
    lineHeight: Platform.OS === 'web' ? 56 : 44,
    fontWeight: fontWeights.bold,
    letterSpacing: -1.7,
    marginBottom: 22,
  } as TextStyle,
  titleMobile: {
    fontSize: 38,
    lineHeight: 44,
    letterSpacing: -1.2,
  } as TextStyle,
  titleAccent: {
    color: accentTextOn('#E85D04'),
  } as TextStyle,
  lead: {
    color: '#5C5652',
    fontFamily: fonts.text,
    fontSize: 16,
    lineHeight: 27,
    marginBottom: 12,
    maxWidth: 520,
  } as TextStyle,
  leadMobile: {
    fontSize: 15,
    lineHeight: 25,
  } as TextStyle,
  actions: {
    flexDirection: 'row',
    gap: 16,
    marginTop: 18,
    flexWrap: 'wrap',
  } as ViewStyle,
  actionsMobile: {
    flexDirection: 'column',
  } as ViewStyle,
  actionBtn: {
    minWidth: 212,
    minHeight: 64,
    borderRadius: 12,
    paddingHorizontal: 18,
    paddingVertical: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    borderWidth: 1,
    ...Platform.select({
      web: { boxShadow: '0 12px 28px rgba(77, 51, 31, 0.08)' },
      default: { elevation: 2 },
    }),
  } as ViewStyle,
  actionMobile: {
    width: '100%',
  } as ViewStyle,
  actionPrimary: {
    backgroundColor: '#F05A00',
    borderColor: '#F05A00',
  } as ViewStyle,
  actionSecondary: {
    backgroundColor: '#FFFFFF',
    borderColor: '#EFE7DE',
  } as ViewStyle,
  actionTextWrap: {
    flex: 1,
  } as ViewStyle,
  actionLabel: {
    color: '#181411',
    fontSize: 14,
    fontWeight: fontWeights.bold,
    lineHeight: 18,
  } as TextStyle,
  actionLabelPrimary: {
    color: ON_ACCENT_TEXT,
  } as TextStyle,
  visualCol: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  } as ViewStyle,
  visualColMobile: {
    flex: 0,
    flexBasis: 'auto',
    flexShrink: 0,
    width: '100%',
  } as ViewStyle,
  orbit: {
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: '#F2DFCE',
    position: 'relative',
  } as ViewStyle,
  photoShell: {
    overflow: 'hidden',
    borderWidth: 10,
    borderColor: '#FFFFFF',
    backgroundColor: '#F8F2EC',
    ...Platform.select({
      web: { boxShadow: '0 22px 70px rgba(137, 90, 50, 0.18)' },
      default: { elevation: 6 },
    }),
  } as ViewStyle,
  heroImage: {
    width: '100%',
    height: '100%',
  } as ImageStyle,
  floatIcon: {
    position: 'absolute',
    width: 56,
    height: 56,
    borderRadius: 28,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 5,
    borderColor: '#FFFFFF',
    ...Platform.select({
      web: { boxShadow: '0 10px 28px rgba(86, 62, 42, 0.12)' },
      default: { elevation: 4 },
    }),
  } as ViewStyle,
});
